/**
 * Phase 13.4a (migration 0236; Corbin 2026-10-19) — the per-site auto-haul switch, on a tank
 * battery (hauler_locations) and on a well's standalone tanks (hauler_wells).
 *
 * Off by default. Auto-haul also needs the AUTO_HAUL plan feature, and each tank a threshold and an
 * auto-haul job type (the tank form). With the feature off the site says so instead of offering a
 * switch the server would refuse; a switch left on from before still shows, so it can be turned off
 * (off is always allowed).
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useFeatureState } from '../../lib/use-features';
import { subText } from './ui-bits';

export function AutoHaulSwitch({
  enabled,
  canManage,
  pending,
  onChange,
  idSuffix,
}: {
  /** Undefined while loading, or from an API that predates 13.4a: nothing is shown. */
  enabled: boolean | undefined;
  canManage: boolean;
  pending: boolean;
  onChange: (next: boolean, done: { onSuccess: () => void; onError: () => void }) => void;
  idSuffix: string;
}): React.JSX.Element | null {
  const feature = useFeatureState('AUTO_HAUL');
  // LOADING / UNKNOWN (the plan could not be read): say nothing rather than guess.
  if (enabled === undefined || feature === 'LOADING' || feature === 'UNKNOWN') return null;
  if (feature === 'OFF' && !enabled) {
    return (
      <p style={subText} data-testid="auto-haul-off-plan">
        Auto-haul is not on your plan.
      </p>
    );
  }
  const id = `auto-haul-${idSuffix}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
      <input
        id={id}
        type="checkbox"
        checked={enabled}
        disabled={!canManage || pending}
        onChange={(e): void => {
          const next = e.target.checked;
          onChange(next, {
            onSuccess: () =>
              toast.success(next ? 'Auto-haul switched on' : 'Auto-haul switched off'),
            onError: () => toast.error('Auto-haul could not be changed. Try again.'),
          });
        }}
      />
      <label htmlFor={id} style={{ fontSize: '14px' }}>
        Auto-haul at this site
      </label>
      <span style={subText} data-testid={feature === 'OFF' ? 'auto-haul-off-plan' : undefined}>
        {feature === 'OFF'
          ? 'Auto-haul is not on your plan, so this does nothing. You can switch it off.'
          : 'A pending haul is created when a tank with a threshold and a job type is predicted to reach it.'}
      </span>
    </div>
  );
}
